<!-- eslint-disable vue/no-v-html -->
<template>
	<PortalHeaderNavMobileGeneric
		v-model:is-open="isMobileMenuOpen"
		variant="left"
	>
		<template #header>
			<NuxtLink
				to="/"
				aria-label="Logo"
				@click="trackHeader.handleEventHeaderClick('Logo', '/')"
			>
				<div class="w-[132px] lg:h-5" v-html="styledLogo" />
			</NuxtLink>
		</template>
		<template #content>
			<section
				class="flex flex-col items-start justify-between border-b border-b-primary-light-4 pb-3"
			>
				<h2
					class="px-5 py-[13.5px] text-base font-normal leading-[24px] text-white"
				>
					Compras
				</h2>
				<div class="flex w-full flex-col">
					<template v-for="option in buyOptions" :key="option.label">
						<PortalHeaderNavMobileOptionLink
							v-if="!option.subOptions"
							:option="option"
							class="px-8 py-[13.5px]"
						/>
						<PortalHeaderNavMobileOptionAccordion
							v-if="option.subOptions"
							:option="option"
						/>
					</template>
				</div>
			</section>
			<section
				class="flex flex-col items-start justify-between border-b border-b-primary-light-4 pb-3"
			>
				<h2
					class="px-5 py-[13.5px] text-base font-normal leading-[24px] text-white"
				>
					Vendas
				</h2>
				<div class="flex w-full flex-col">
					<template v-for="option in sellOptions" :key="option.label">
						<PortalHeaderNavMobileOptionLink
							v-if="!option.subOptions"
							:option="option"
							class="px-8 py-[13.5px]"
						/>
					</template>
				</div>
			</section>
			<section class="flex flex-col items-start justify-between pt-1">
				<PortalHeaderNavMobileOptionLink
					:option="blogOption"
					class="w-full px-8 py-[13.5px]"
					@click="handleClickBlog"
				/>
				<PortalHeaderNavMobileOptionLink
					:option="aboutPilarOption"
					class="w-full px-8 py-[13.5px]"
					@click="handleClickAboutPilar"
				/>
			</section>
		</template>
		<template #footer>
			<ClientOnly>
				<template #fallback>
					<!-- SSR fallback: Always show login button to prevent hydration mismatch -->
					<Button
						:variant="null"
						:is-client-only="false"
						class="h-[50px] w-full min-w-[100px] gap-1 border-none bg-primary-light-4 p-0 text-white"
					>
						<IconMaterialSymbolsLightPersonOutline
							class="ml-[-10px] size-6 text-beige"
						/>
						<span class="text-sm font-medium leading-[20px]">
							{{ t('login') }}
						</span>
					</Button>
				</template>

				<!-- Client: Show profile or login based on actual auth state -->
				<NuxtLink
					v-if="loggedIn"
					to="/perfil"
					@click="navigationStore.closeMenus"
				>
					<PortalHeaderNavProfileOption variant="mobile" bg-variant="primary">
						<template #icon>
							<IconMaterialSymbolsLightChevronRight
								class="ml-auto size-6 text-beige"
							/>
						</template>
					</PortalHeaderNavProfileOption>
				</NuxtLink>
				<Button
					v-if="!loggedIn"
					:variant="null"
					class="h-[50px] w-full min-w-[100px] gap-1 border-none bg-primary-light-4 p-0 text-white"
					@click="handleLoginClick"
				>
					<IconMaterialSymbolsLightPersonOutline
						class="ml-[-10px] size-6 text-beige"
					/>
					<span class="text-sm font-medium leading-[20px]">
						{{ t('login') }}
					</span>
				</Button>
			</ClientOnly>
		</template>
	</PortalHeaderNavMobileGeneric>
</template>

<script setup lang="ts">
import type { HeaderProps } from '.';

import { useEvents } from '~/composables/events/useEvents';
import { useShortsNavigation } from '~/composables/shorts';
import { useFeatureFlags } from '~/composables/useFeatureFlags';
import { useLoginModal } from '~/composables/useLoginModal';
import { useNavigationStore } from '~/store/navigationStore';
import { resolveBuyOptionsWithFlags } from '~/utils/index';

import IconMaterialSymbolsLightChevronRight from '~icons/material-symbols-light/chevron-right';
import LinearPilarLogoRaw from '~icons/pilar/linear-logo?raw&width=1em&height=1em';

import { exploreByRegionOption, highlightBuyOptions, sellOptions } from '.';
import messages from './PortalHeader.locale.json';
import PortalHeaderNavMobileAboutPilarButton from './PortalHeaderNavMobileAboutPilarButton.vue';
import PortalHeaderNavMobileBlogPilarButton from './PortalHeaderNavMobileBlogPilarButton.vue';

const { trackHeader } = useEvents();
const { navigateToShortsWithContext } = useShortsNavigation();
const { ft_shorts_access_points, ft_temp_shorts_new_menu } = useFeatureFlags();
const { openLoginModal } = useLoginModal();

defineProps<{ variant: HeaderProps['variant'] }>();

const { t } = useI18n({
	useScope: 'local',
	messages
});

const { loggedIn } = useUserSession();
const navigationStore = useNavigationStore();
const { isMobileMenuOpen } = storeToRefs(navigationStore);

const handleClickAboutPilar = () => {
	trackHeader.handleEventHeaderClick('Sobre a Pilar', '/sobre-a-pilar');
};

const handleClickBlog = () => {
	trackHeader.handleEventHeaderClick('Blog', 'https://blog.pilarhomes.com.br');
};

/**
 * Handles login button click with tracking and modal opening
 * Uses double requestAnimationFrame to ensure tracking runs AFTER paint
 */
function handleLoginClick(): void {
	// Open modal first for immediate visual feedback
	openLoginModal();
	// Double rAF ensures we yield to browser for paint, then run tracking
	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			trackHeader.handleEventHeaderClick('Entrar');
		});
	});
}

// `useFeatureFlags()` returns plain (non-reactive) snapshots, so buyOptions
// has no reactive dependencies and can be a const instead of a computed.
const buyOptions = [
	...resolveBuyOptionsWithFlags(
		highlightBuyOptions,
		{
			shortsAccessPoints: ft_shorts_access_points,
			shortsNewMenuBadge: ft_temp_shorts_new_menu
		},
		navigateToShortsWithContext
	),
	exploreByRegionOption
];

const aboutPilarOption = {
	label: PortalHeaderNavMobileAboutPilarButton,
	route: '/sobre-a-pilar'
};

const blogOption = {
	label: PortalHeaderNavMobileBlogPilarButton,
	route: 'https://blog.pilarhomes.com.br',
	openInNewTab: true
};

const styledLogo = (LinearPilarLogoRaw as unknown as string).replace(
	/<svg/,
	'<svg width="100%" height="100%"'
);
</script>
